/**
 * Renders one {@link AsyncState}, and insists the caller names its empty case.
 *
 * The four states were four ternaries per panel and the empty one was usually
 * missing, so a region holding nothing rendered as blank space. `empty` is
 * therefore required; loading and error have defaults, having nothing does not.
 *
 * Nothing is wrapped in an element: a wrapper becomes the flex or grid item
 * wherever a boundary sits in a laid-out parent, and its children stop
 * participating in that layout.
 *
 * @module components/ui/AsyncBoundary
 */

import { type ReactElement, type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { Alert } from "./Alert";
import { Button } from "./Button";
import { EditLockExempt } from "./EditLock";
import { LoadingState } from "./LoadingState";
import type { AsyncState } from "./useAsync";

export interface AsyncBoundaryProps<T> {
  state: AsyncState<T>;
  /** What this region shows when it holds nothing. Required on purpose. */
  empty: ReactNode;
  /** Names what is loading. Defaults to a generic wait. */
  loadingLabel?: string;
  /**
   * A skeleton shaped like the content that is coming, in place of the generic
   * bars. Pass one wherever the shape is known: a card, a row, a table.
   */
  loading?: ReactNode;
  /** Replaces the default error card. */
  /**
   * `retry` is absent when no `onRetry` was given, so a custom renderer can
   * hide its own control rather than draw a button that does nothing.
   */
  error?: (error: Error, retry?: () => void) => ReactNode;
  /** Offered by the default error card when given. */
  onRetry?: () => void;
  children: (data: T) => ReactNode;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <AsyncBoundary state={rounds} empty={<EmptyState title="No review rounds yet" />}>
 *   {(data) => <RoundList rounds={data} />}
 * </AsyncBoundary>
 * ```
 */
export function AsyncBoundary<T>({
  state,
  empty,
  loadingLabel = "Loading",
  loading = undefined,
  error = undefined,
  onRetry = undefined,
  children,
  "data-testid": testId,
}: AsyncBoundaryProps<T>): ReactElement {
  // One region, present on every branch, holding text only while loading. A
  // live region that enters the DOM already containing its text is generally
  // not announced — a screen reader watches an existing region for a change —
  // so this has to outlive the branch it describes. `LoadingState` is told to
  // stay `silent` for the same reason: two regions is a duplicate at best.
  // The region mounts empty and is filled a tick later. A screen reader watches
  // an existing region for a change, so one that arrives already holding its
  // text is not announced — and `useAsync` starts at `loading`, which makes the
  // very first render of every panel exactly that case. Portalled to the body
  // as well, because the boundary deliberately adds no wrapper: its children
  // stay items of the parent's own grid, and an inline region would be one
  // more child.
  const region = useRef<HTMLSpanElement>(null);
  const message = state.status === "loading" ? loadingLabel : "";
  useEffect(() => {
    // Written imperatively, not through state: a setState here would cascade a
    // render, which `react-hooks/set-state-in-effect` forbids across this tree.
    // A live region is one of the few places a direct DOM write is the right
    // tool — the announcement is the side effect, not the render.
    if (region.current) {
      region.current.textContent = message;
    }
  }, [message]);

  const announcement =
    typeof document === "undefined"
      ? null
      : createPortal(
          <span ref={region} className="sr-only" role="status" aria-live="polite" />,
          document.body,
        );

  const branch = (): ReactNode => {
    if (state.status === "loading") {
      return loading ?? <LoadingState label={loadingLabel} data-testid={testId} silent />;
    }
    if (state.status === "error") {
      return error ? (
        error(state.error, onRetry)
      ) : (
        // Inline and persistent, never a toast: an error the reader has to act
        // on cannot dismiss itself while they are reading it.
        <Alert tone="error" icon="warning-circle" data-testid={testId}>
          <span className="flex flex-wrap items-center gap-3">
            <span>{state.error.message}</span>
            {onRetry && (
              <EditLockExempt>
                <Button variant="ghost" size="sm" onClick={onRetry}>
                  Try again
                </Button>
              </EditLockExempt>
            )}
          </span>
        </Alert>
      );
    }
    if (state.status === "empty") {
      return empty;
    }
    return children(state.data);
  };

  return (
    <>
      {announcement}
      {branch()}
    </>
  );
}
